import { OPERATORS } from '../../constants';
import { BaseOperatorStrategy } from './base.operator-strategy';
import type {
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
} from '../operator-strategy';

/**
 * AISstream pricing & constraint strategy.
 *
 * PRICING MODEL — flat per DAY of stream, area-independent:
 *   unitcost     = USD price of ONE DAY (operator doc `costunit: "day"`)
 *   selectedCost = unitcost × days of TOI
 *
 * AISstream is a continuous SSE feed held open for the length of the TOI, so
 * what a customer buys is a SPAN OF DAYS — not a refresh, and not an area. A
 * harbour box and an ocean basin cost the same per day.
 *
 * MUST AGREE WITH THE BACKEND. `controllers/budget.js` prices this operator as
 * `findTier.unitcost × ceil(TOI / 1 day)`, floored at one day. `streamDaysForToi`
 * below is that formula, and the two have to stay identical: this is what the
 * customer is quoted, that is what they are invoiced, and a disagreement of one
 * day at a boundary is a support ticket. The rule is stated once, here, and the
 * spec beside this file is what pins it.
 *
 * No holdback / cloud-cover / ONA factors apply — there is no image to be
 * cloudy and nothing is withheld from a live feed — which is why
 * `calculateUnitCost` is overridden rather than left to the base class's factor
 * maths.
 *
 * Constraints: open-water areas only.
 *
 * Registered in OperatorStrategyFactory.getStrategy() under OPERATORS.AISSTREAM.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Days of stream a TOI buys — the client half of the price, and a mirror of the
 * `streamDays` calculation in the backend's `controllers/budget.js`.
 *
 * `ceil`, and never below 1: a part-day of stream is a day of stream, and a
 * same-day TOI must not be free. An unparseable or inverted window falls to one
 * day rather than to NaN — a quote reading "US$ NaN" is worse than one reading
 * low, and the window is validated before it reaches here.
 *
 * Exported so the spec can exercise the boundaries without standing up a
 * strategy, a component or a TestBed.
 */
export function streamDaysForToi(openWindow: any, closeWindow: any): number {
  const spanMs = new Date(closeWindow).getTime() - new Date(openWindow).getTime();
  return Number.isFinite(spanMs) && spanMs > 0 ? Math.ceil(spanMs / MS_PER_DAY) : 1;
}

/**
 * How many days this row is priced for.
 *
 * Prefers an explicit `streamDays` the host component may have set, and falls
 * back to deriving it from the TOI carried on the row. Both are supported on
 * purpose: the row-carried window is what makes this work without every caller
 * remembering to precompute, and the explicit field is the escape hatch for a
 * caller that already knows the answer.
 */
export function streamDaysForRow(sensorRow: any): number {
  const explicit = Number(sensorRow?.streamDays);
  if (Number.isFinite(explicit) && explicit > 0) return Math.ceil(explicit);
  return streamDaysForToi(sensorRow?.openWindow, sensorRow?.closeWindow);
}

export class AisstreamOperatorStrategy extends BaseOperatorStrategy {
  readonly operatorKey = OPERATORS.AISSTREAM;

  /**
   * Set `unitcost` to the per-day price and `selectedCost` to price × days.
   *
   * WHY THIS IS AN OVERRIDE. The base class runs `computeBaseCost` through
   * `applyFactors` (holdback / cloud / ONA), none of which mean anything for a
   * live AIS feed, and then writes the SAME number to both `unitcost` and
   * `selectedCost`. This operator needs them to differ: the tier column shows
   * the day rate, the submission total is the day rate times the TOI.
   *
   * It also fixes what the previous implementation actually did. It read
   * `sensorRow.unitcost` — a field the host components seed to 0 and never
   * assign (`let unitcost = 0` in both row builders) — and the base class then
   * wrote the result straight back to it. Reading a field you are about to
   * overwrite makes zero a fixed point, so the row priced at 0 no matter what
   * the operator document said. Every other strategy reads the LATENCY TIER,
   * which is the field that carries a real price, and so does this one now.
   */
  override calculateUnitCost({ sensorRow, latencyIndx }: UnitCostCalculationArgs): void {
    if (!sensorRow) return;

    const tierIndex = Number.isInteger(latencyIndx) && latencyIndx >= 0 ? latencyIndx : 0;
    const unitPrice = Number(
      sensorRow.latency?.[tierIndex]?.unitcost ?? sensorRow.latency?.[0]?.unitcost ?? 0,
    );
    const days = streamDaysForRow(sensorRow);

    // Keep the tier's own display price on the day rate. Nothing about an
    // AISstream day varies by tasking tier — there is nothing to schedule and
    // nothing to downlink — so a row that grew a second tier must not show a
    // different number against it.
    if (Array.isArray(sensorRow.latency) && sensorRow.latency[tierIndex]) {
      sensorRow.latency[tierIndex].cost = unitPrice;
      sensorRow.latency[tierIndex].unitcost = unitPrice;
    }

    sensorRow.unitcost = unitPrice;           // per DAY — what the unit column states
    sensorRow.selectedCost = unitPrice * days; // total — what submission sends

    // The base class applies `userCost` after its own maths; do the same here so
    // a customer-specific multiplier is not silently dropped by this override.
    if (sensorRow.userCost > 0 && sensorRow.selectedCost > 0) {
      sensorRow.selectedCost = sensorRow.selectedCost * sensorRow.userCost;
    }
  }

  /**
   * Kept for the base class's `recalculateSelectedCost` path and for parity with
   * the other strategies; `calculateUnitCost` above takes precedence on every
   * live pricing route.
   */
  protected computeBaseCost(sensorRow: any, latencyIndx: number): number | null {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return null;
    const unitcost = Number(sensorRow.latency[latencyIndx]?.unitcost ?? 0);
    return unitcost * streamDaysForRow(sensorRow);
  }

  getSelectionBlocker(args: SelectionBlockerArgs): SelectionBlockerResult | null {
    if (!args.taskingContext) return null;
    // AIS data only exists over open water
    if (args.sensorRow?.openWater === false) {
      return {
        code:    'AISSTREAM_LAND_TARGET',
        message: 'AISstream vessel tracking is only available for open-water areas. Please select a maritime region.',
      };
    }
    return null;
  }
}
